import { useCallback, useEffect, useState } from "react";
import {
  getActivePostHogSurveys,
  trackSurveyDismissed,
  trackSurveySent,
  trackSurveyShown,
  type PostHogSurvey,
} from "../integrations/analytics/client";

const DISMISSED_SURVEYS_KEY = "nfp_tracker_dismissed_surveys";
const COMPLETED_SURVEYS_KEY = "nfp_tracker_completed_surveys";

const getStoredSet = (key: string): Set<string> => {
  try {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const storeInSet = (key: string, id: string) => {
  try {
    const existing = getStoredSet(key);
    existing.add(id);
    const json = JSON.stringify(Array.from(existing));
    sessionStorage.setItem(key, json);
    localStorage.setItem(key, json);
  } catch (error) {
    console.error("[PostHogSurveys] Failed to store survey state", error);
  }
};

export const usePostHogSurvey = () => {
  const [surveys, setSurveys] = useState<PostHogSurvey[]>([]);
  const [activeSurvey, setActiveSurvey] = useState<PostHogSurvey | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  const checkSurveys = useCallback(() => {
    getActivePostHogSurveys((fetchedSurveys) => {
      setSurveys(fetchedSurveys || []);

      const dismissed = getStoredSet(DISMISSED_SURVEYS_KEY);
      const completed = getStoredSet(COMPLETED_SURVEYS_KEY);

      // Find the first eligible survey that hasn't been completed or dismissed
      const eligible = (fetchedSurveys || []).find(
        (s) => !dismissed.has(s.id) && !completed.has(s.id)
      );

      if (eligible && !hasBeenShown) {
        setActiveSurvey(eligible);
        setIsOpen(true);
        setHasBeenShown(true);
        trackSurveyShown(eligible);
      }
    });
  }, [hasBeenShown]);

  useEffect(() => {
    // Initial fetch after a brief delay to allow PostHog SDK to load remote configs
    const timer = setTimeout(() => {
      checkSurveys();
    }, 1500);

    return () => clearTimeout(timer);
  }, [checkSurveys]);

  const submitResponse = useCallback(
    (responses: Record<string, unknown>) => {
      if (!activeSurvey) return;

      trackSurveySent(activeSurvey, responses);
      storeInSet(COMPLETED_SURVEYS_KEY, activeSurvey.id);

      setTimeout(() => {
        setIsOpen(false);
        setActiveSurvey(null);
      }, 1200);
    },
    [activeSurvey]
  );

  const dismissSurvey = useCallback(() => {
    if (!activeSurvey) return;

    trackSurveyDismissed(activeSurvey);
    storeInSet(DISMISSED_SURVEYS_KEY, activeSurvey.id);
    setIsOpen(false);
    setActiveSurvey(null);
  }, [activeSurvey]);

  return {
    surveys,
    activeSurvey,
    isOpen,
    setIsOpen,
    submitResponse,
    dismissSurvey,
    checkSurveys,
  };
};
